import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { ListConfigsFilters, UpdateSystemConfigRequestDto } from '@/api/client';
import {
  mapConfigDefinition,
  mapConfigItem,
  type ConfigDefinitionViewModel,
  type ConfigItemViewModel,
} from '@/features/admin-configs/api';

import { platformConfigsEndpoints } from './endpoints';

/**
 * GLOBAL-plane (deployment-wide) configuration store (PTV2-1383) — the platform-console
 * counterpart of the per-tenant `admin-configs` hooks. Same catalog-driven shape and the
 * same view models / mappers (reused from `@/features/admin-configs/api`); only the transport
 * (`/api/v1/global-configs`) and the react-query key namespace (`['platform','configs',…]`)
 * differ. Scope is implicitly GLOBAL, so no scope filter is exposed.
 */

/** GLOBAL-scope list filters — scope is fixed by the endpoint. */
export type GlobalConfigFilters = Omit<ListConfigsFilters, 'scope'>;

export const platformConfigsRootKey = ['platform', 'configs'] as const;
export const platformConfigCatalogQueryKey = [...platformConfigsRootKey, 'catalog'] as const;

function normalizeFilters(filters: GlobalConfigFilters): Required<GlobalConfigFilters> {
  return {
    q: filters.q ?? '',
    namespace: filters.namespace ?? '',
    tag: filters.tag ?? '',
    diffOnly: filters.diffOnly ?? false,
  };
}

export function platformConfigListQueryKey(
  filters: GlobalConfigFilters,
): readonly [...typeof platformConfigsRootKey, 'list', Required<GlobalConfigFilters>] {
  return [...platformConfigsRootKey, 'list', normalizeFilters(filters)] as const;
}

export function useGlobalConfigCatalog(): UseQueryResult<ConfigDefinitionViewModel[]> {
  return useQuery<ConfigDefinitionViewModel[]>({
    queryKey: platformConfigCatalogQueryKey,
    queryFn: async ({ signal }) => {
      const list = await platformConfigsEndpoints.getGlobalCatalog({ signal });
      return list.map(mapConfigDefinition);
    },
  });
}

export function useGlobalConfigList(
  filters: GlobalConfigFilters,
): UseQueryResult<ConfigItemViewModel[]> {
  const normalized = normalizeFilters(filters);
  return useQuery<ConfigItemViewModel[]>({
    queryKey: platformConfigListQueryKey(filters),
    queryFn: async ({ signal }) => {
      const list = await platformConfigsEndpoints.listGlobalConfigs(
        {
          ...(normalized.q !== '' ? { q: normalized.q } : {}),
          ...(normalized.namespace !== '' ? { namespace: normalized.namespace } : {}),
          ...(normalized.tag !== '' ? { tag: normalized.tag } : {}),
          ...(normalized.diffOnly ? { diffOnly: true } : {}),
        },
        { signal },
      );
      return list.map(mapConfigItem);
    },
  });
}

export interface UpsertGlobalConfigInput {
  readonly key: string;
  readonly value: string;
  readonly reason?: string;
}

export function useGlobalConfigMutations(): {
  readonly upsert: UseMutationResult<ConfigItemViewModel, Error, UpsertGlobalConfigInput>;
  readonly reset: UseMutationResult<undefined, Error, string>;
} {
  const queryClient = useQueryClient();

  // After any write, value + source/isOverridden/audit fields can all change and the backend
  // evicts its own read-through cache, so a refetch under the root key is the only correct
  // reconciliation.
  const invalidateAll = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: platformConfigsRootKey });
  };

  const upsert = useMutation<ConfigItemViewModel, Error, UpsertGlobalConfigInput>({
    mutationFn: async ({ key, value, reason }) => {
      const body: UpdateSystemConfigRequestDto = {
        value,
        ...(reason !== undefined && reason !== '' ? { reason } : {}),
      };
      return mapConfigItem(await platformConfigsEndpoints.upsertGlobalConfig(key, body));
    },
    onSettled: () => {
      invalidateAll().catch(() => undefined);
    },
  });

  const reset = useMutation<undefined, Error, string>({
    mutationFn: (key) => platformConfigsEndpoints.resetGlobalConfig(key),
    onSettled: () => {
      invalidateAll().catch(() => undefined);
    },
  });

  return { upsert, reset };
}
